import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * Interaction model: submenus open on both CLICK and HOVER. Hovering a
 * top-level item with a submenu (mouse only, not touch) opens it after a
 * moment; moving the pointer away closes it after a short grace period,
 * cancelled if the pointer reaches the submenu panel itself, so it doesn't
 * flicker shut while crossing the gap. Clicking a top-level item toggles
 * it open/closed explicitly (and is what drives the interaction on
 * touch); clicking a different item switches to it; clicking anywhere
 * outside the menu (or pressing Escape) closes it. A plain link with
 * nothing to expand (e.g. "Sale", "About Us") is never intercepted and
 * just navigates normally.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Pending "close after hover grace period" timer, or null when none is
   * scheduled. Cancelled whenever the pointer re-enters the trigger item
   * or its submenu, so a submenu never closes mid-crossing.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #hoverCloseTimeout = null;

  /**
   * The top-level `<li>` elements hover listeners are attached to, kept
   * around so `disconnectedCallback` can remove exactly what was added.
   * @type {Element[]}
   */
  #hoverItems = [];

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#outsideClickListener);
    document.addEventListener('keydown', this.#keydownListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.overflowMenu?.addEventListener('pointerenter', this.#clearHoverCloseTimeout);

    // The theme's declarative `on:eventname="/method"` template bindings
    // (used elsewhere in this file for focus/blur) don't cover click or
    // pointerenter/pointerleave, so click-to-open and hover-to-open are
    // both wired up here with normal native listeners instead — these are
    // the ones actually driving open/close.
    this.addEventListener('click', this.toggle);

    // pointerenter/pointerleave don't bubble, so each top-level item gets
    // its own pair of listeners (mirrors the light-DOM query the "more"
    // item's own hover-close listener already relies on below).
    this.#hoverItems = Array.from(this.querySelectorAll('.menu-list__list-item'));
    this.#hoverItems.forEach((li) => {
      li.addEventListener('pointerenter', this.hoverEnter);
      li.addEventListener('pointerleave', this.hoverLeave);
    });

    // Cancel the hover-close grace period as soon as the pointer reaches
    // a submenu panel, so moving diagonally from the trigger down into
    // its content never closes the menu mid-crossing.
    this.refs.submenu?.forEach((submenu) => {
      submenu.addEventListener('pointerenter', this.#clearHoverCloseTimeout);
      submenu.addEventListener('pointerleave', this.hoverLeave);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#outsideClickListener);
    document.removeEventListener('keydown', this.#keydownListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.overflowMenu?.removeEventListener('pointerenter', this.#clearHoverCloseTimeout);
    this.removeEventListener('click', this.toggle);
    this.#hoverItems.forEach((li) => {
      li.removeEventListener('pointerenter', this.hoverEnter);
      li.removeEventListener('pointerleave', this.hoverLeave);
    });
    this.#hoverItems = [];
    this.refs.submenu?.forEach((submenu) => {
      submenu.removeEventListener('pointerenter', this.#clearHoverCloseTimeout);
      submenu.removeEventListener('pointerleave', this.hoverLeave);
    });
    this.#clearHoverCloseTimeout();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Closes the open submenu when the user clicks anywhere outside this
   * menu component. Makes "click away to close" reliable regardless of
   * how the submenu was opened.
   * @param {MouseEvent} event
   */
  #outsideClickListener = (event) => {
    if (!this.#state.activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    // The "more" overflow panel is rendered inside a shadow root, so it
    // isn't a light-DOM descendant of `this` — check it separately.
    if (this.overflowMenu?.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * Closes the open submenu on Escape, for keyboard users.
   * @param {KeyboardEvent} event
   */
  #keydownListener = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#state.activeItem) return;
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Click handler bound on each top-level menu item (and the "more" item).
   * Resolves which item was clicked from the real click target — robust
   * to the click landing on the link text, the chevron icon, or the item's
   * own padding — and opens/closes/switches accordingly. A click on a
   * plain link with no submenu is left completely alone so it navigates.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const moreItemEl = event.target.closest('[slot="more"]');
    const item = moreItemEl ? findMenuItem(moreItemEl) : event.target.closest('[ref="menuitem"]');

    if (!item) return;

    const hasSubmenu = Boolean(findSubmenu(item));

    // Nothing to expand (e.g. "Sale", "About Us") — let it navigate.
    if (!hasSubmenu && !moreItemEl) return;

    event.preventDefault();

    if (this.#state.activeItem === item) {
      this.#deactivate(item);
      return;
    }

    const li = item.parentElement;
    const isDefaultSlot = !moreItemEl && li?.slot === '';

    this.#openItem(item, isDefaultSlot);
  };

  /**
   * Opens a submenu on hover (mouse pointers only — touch keeps relying
   * on `toggle`). Attached directly on each top-level `<li>`, so
   * `event.currentTarget` is always that `<li>` regardless of which
   * descendant (link text, "more" button) the pointer actually entered
   * on. Re-entering the already-open item is a no-op.
   * @param {PointerEvent} event
   */
  hoverEnter = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!this.headerComponent) return;

    const li = event.currentTarget;
    if (!(li instanceof Element)) return;

    this.#clearHoverCloseTimeout();

    const item = findMenuItem(li);
    if (!item || item === this.#state.activeItem) return;

    const isMoreItem = li.matches('[slot="more"]');
    const hasSubmenu = Boolean(findSubmenu(item));
    if (!hasSubmenu && !isMoreItem) return;

    const isDefaultSlot = !isMoreItem && li.slot === '';

    this.#openItem(item, isDefaultSlot);
  };

  /**
   * Schedules the hover-opened submenu to close after a short grace
   * period, giving the pointer time to travel from the trigger down into
   * the submenu panel. `#clearHoverCloseTimeout` cancels this if the
   * pointer reaches the trigger or the submenu again before it fires.
   * @param {PointerEvent} event
   */
  hoverLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!this.#state.activeItem) return;

    this.#clearHoverCloseTimeout();
    const itemToClose = this.#state.activeItem;
    this.#hoverCloseTimeout = setTimeout(() => {
      this.#hoverCloseTimeout = null;
      this.#deactivate(itemToClose);
    }, 250);
  };

  /**
   * Cancels any pending hover-close timer.
   */
  #clearHoverCloseTimeout = () => {
    if (this.#hoverCloseTimeout !== null) {
      clearTimeout(this.#hoverCloseTimeout);
      this.#hoverCloseTimeout = null;
    }
  };

  /**
   * Open the submenu for an item on keyboard focus, mirroring the click
   * behaviour so tabbing through the nav works the same way.
   * @param {FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.#openItem(item, isDefaultSlot);
  };

  /**
   * Shared open logic used by both click (`toggle`) and keyboard focus
   * (`activate`), once the target item has been resolved.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   */
  #openItem(item, isDefaultSlot) {
    if (!this.headerComponent) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active item on blur (keyboard navigation moving away).
   * @param {FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
